// Define an enum for the environment modes
enum ConsoleEnvironment {
  development = 'development',
  production = 'production',
  test = 'test'
}

// Define an enum for the log levels
enum LogLevel {
  error = 'error',
  warn = 'warn',
  info = 'info',
  debug = 'debug'
}

// Define a class for the console logger
export class ConsoleLogger {
  // Declare a private static property for the current environment
  private static environment: ConsoleEnvironment;

  // Declare a private static property for the current log level
  private static logLevel: LogLevel;

  // Define a static method for setting the environment based on the window.href
  static setEnvironment() {
    // Get the window.href value
    const href = window.location.href;
    // Check if the href contains "localhost:4200"
    if (href.includes('localhost:4200')) {
      // Set the environment to development
      this.environment = ConsoleEnvironment.development;
      // Call the setLogLevel method to initialize the log level
      this.setLogLevel('debug');
    } else {
      // Set the environment to production
      this.environment = ConsoleEnvironment.production;
    }
  }

  // Define a static method for setting the log level based on a string argument
  static setLogLevel(level: string) {
    // Check if the level is a valid log level
    if (Object.values(LogLevel).includes(level as LogLevel)) {
      // Cast the level to LogLevel and assign it to the property
      this.logLevel = level as LogLevel;
    } else {
      // Throw an error if the level is invalid
      throw new Error('Invalid log level');
    }
  }

  // Define some different static functions for each log level that call the log method with the corresponding level and objects

  // A function for logging error messages
  static error(...objects: any[]) {
    this.log(LogLevel.error, ...objects);
  }

  // A function for logging warning messages
  static warn(...objects: any[]) {
    this.log(LogLevel.warn, ...objects);
  }

  // A function for logging info messages
  static info(...objects: any[]) {
    this.log(LogLevel.info, ...objects);
  }

  // A function for logging debug messages
  static debug(...objects: any[]) {
    this.log(LogLevel.debug, ...objects);
  }

  // Define a static method for logging messages with a log level argument and a rest parameter for multiple objects
  static log(level: LogLevel, ...objects: any[]) {
    // Check if the environment is development and the log level is equal or higher than the current log level
    if (this.environment === ConsoleEnvironment.development && this.compareLogLevel(this.logLevel, level) >= 0) {
      // Use the console.log method to print the log level prefix and the objects
      switch (level) {
        case LogLevel.debug:
          try {
            // eslint-disable-next-line no-console
            console.debug(...objects);
          } catch (e) {
            console.log(`[${level}]`, ...objects);
          }
          this.showCallStack();
          break;
        case LogLevel.error:
          console.error(...objects);
          break;
        case LogLevel.info:
          try {
            // eslint-disable-next-line no-console
            console.info(...objects);
          } catch (e) {
            console.log(`[${level}]`, ...objects);
          }
          break;
        case LogLevel.warn:
          console.warn(...objects);
          break;
        default:
          console.log(`[${level}]`, ...objects);
      }
    }
  }

  // Define a private static helper method for comparing log levels and returning an integer
  private static compareLogLevel(level1: LogLevel, level2: LogLevel) {
    // Define an array of log levels in descending order of priority
    const levels = [LogLevel.error, LogLevel.warn, LogLevel.info, LogLevel.debug];
    // Get the indices of the two log levels in the array
    const index1 = levels.indexOf(level1);
    const index2 = levels.indexOf(level2);
    // Return the difference between the indices
    return index1 - index2;
  }

  private static showCallStack() {
    try {
      // Create an Error object
      const error = new Error();
      if (!error.stack) {
        return;
      }
      // Get the stack trace as an array of lines
      const stack = error.stack.split('\n');
      // Remove the first two lines (the error message and the log function itself)
      stack.shift();
      stack.shift();
      stack.shift();
      stack.shift();
      // Keep only the first three lines of the remaining stack
      const trace = stack.slice(0, 3);
      // Join the trace lines with a newline character
      const traceString = trace.join('\n');
      // Print the arguments and the trace with console.log
      try {
        // eslint-disable-next-line no-console
        console.debug(traceString);
      } catch (e) {
        console.log(traceString);
      }
    } catch (e) {
      // do nothing
    }
  }
}

// Call the setLogLevel method to initialize the log level
ConsoleLogger.setLogLevel('info');

// Call the setEnvironment method to initialize the environment
ConsoleLogger.setEnvironment();
